import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GripVertical, LayoutList, Menu as MenuIcon, RotateCcw } from "lucide-react";

export function TabSettings({ open, onOpenChange, allTabs, bar, menu, onSave }) {
  const LABEL = Object.fromEntries(allTabs);
  const ALL_IDS = allTabs.map((t) => t[0]);
  const [localBar, setLocalBar] = useState(bar);
  const [localMenu, setLocalMenu] = useState(menu);
  const [dragId, setDragId] = useState(null);

  useEffect(() => { if (open) { setLocalBar(bar); setLocalMenu(menu); } }, [open, bar, menu]);

  const place = (id, targetList, beforeId) => {
    if (!id || id === "overview") return;
    const b = localBar.filter((x) => x !== id);
    const m = localMenu.filter((x) => x !== id);
    const arr = targetList === "bar" ? b : m;
    let idx = beforeId ? arr.indexOf(beforeId) : arr.length;
    if (idx < 0) idx = arr.length;
    if (targetList === "bar" && idx === 0 && arr[0] === "overview") idx = 1;
    arr.splice(idx, 0, id);
    setLocalBar(b); setLocalMenu(m);
  };

  const reset = () => { setLocalBar(ALL_IDS.slice()); setLocalMenu([]); };

  const renderItem = (id, list) => (
    <div
      key={id}
      draggable={id !== "overview"}
      onDragStart={() => setDragId(id)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.stopPropagation(); place(dragId, list, id); }}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm select-none ${id === "overview" ? "border-[#e2e8f0] bg-slate-50 text-slate-400" : "border-[#e2e8f0] bg-white text-slate-700 cursor-grab hover:border-sky-300 active:cursor-grabbing"}`}
      data-testid={`ts-item-${id}`}
    >
      <GripVertical className="w-4 h-4 text-slate-300" />
      <span className="truncate">{LABEL[id]}</span>
      {id === "overview" && <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">fast</span>}
    </div>
  );

  const Column = ({ title, icon, list, items, hint }) => (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{icon}{title}</div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => place(dragId, list, null)}
        className="space-y-2 rounded-xl border border-dashed border-[#e2e8f0] bg-[#f8fafc] p-2 min-h-[200px]"
        data-testid={`ts-col-${list}`}
      >
        {items.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">{hint}</p>
        ) : items.map((id) => renderItem(id, list))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#e2e8f0] max-w-2xl" data-testid="tab-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">Tilpas faner</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">Træk fanerne for at bestemme rækkefølgen, eller flyt dem over i "Mere-menu" for at rydde op i fanelinjen. "Oversigt" er altid synlig.</p>
        <div className="flex flex-col sm:flex-row gap-4 mt-2">
          <Column title="Fanelinje" icon={<LayoutList className="w-4 h-4" />} list="bar" items={localBar} hint="Træk faner hertil" />
          <Column title="Mere-menu (dropdown)" icon={<MenuIcon className="w-4 h-4" />} list="menu" items={localMenu} hint="Træk faner hertil for at skjule dem i en menu" />
        </div>
        <div className="flex items-center justify-between gap-2 mt-4">
          <Button variant="ghost" onClick={reset} className="text-slate-500 gap-1.5" data-testid="ts-reset-btn"><RotateCcw className="w-4 h-4" /> Nulstil</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-[#e2e8f0] text-slate-700" data-testid="ts-cancel-btn">Annullér</Button>
            <Button onClick={() => { onSave(localBar, localMenu); onOpenChange(false); }} className="bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120]" data-testid="ts-save-btn">Gem</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TabSettings;
